import type { LandscapeRunStatus } from '@/types/landscape-log';

/**
 * Everything a `.landscape.status.updated` broadcast decides: the wire-to-run
 * mapping and whether the event invalidates `['landscapes']` or patches
 * `status_info` into the cache (`WEB-004`,
 * `components/work-run-log.md § The three hand-written mirrors…`,
 * `data-flow.md § Flow: landscape build`, hop 8). `ProtectedRoute.tsx` owns
 * the subscription and the two effects below; the decision of *what to do*
 * lives here so it is testable without mounting the app shell or a real
 * `QueryClient`.
 *
 * `src/services/echo.service.ts` is unedited by this task, so its
 * `LandscapeStatusEvent` interface stays unexported. The shape below is a
 * structural match, not an import -- `subscribeToLandscapeUpdates`'s
 * callback parameter is assignable to it without either side importing the
 * other.
 */
export interface LandscapeBroadcastPayload {
  landscape_id: number;
  status: string;
  status_info: string;
}

/**
 * The wire vocabulary (`App\Enums\LandscapeStatus`) mapped onto the run
 * vocabulary the cache actually holds (`constraints.md § C-8`). `null` means
 * "cannot be mapped" -- the three terminal wire values, plus the
 * never-emitted `completed` the client already branched on, none of which
 * carry the keyword count a terminal `LandscapeRunStatus` needs. Any other,
 * unrecognised wire value maps to `null` too: invalidating and re-reading the
 * real row is always safe, silently mis-mapping it is not.
 */
export function mapLandscapeBroadcastStatus(wireStatus: string): LandscapeRunStatus | null {
  switch (wireStatus) {
    case 'pending':
      return 'queued';
    case 'processing':
      return 'running';
    default:
      return null;
  }
}

export interface LandscapeBroadcastEffects {
  /**
   * The `LandscapeRunStatus` already in the cache for this landscape id,
   * read across every query under the `['landscapes']` prefix. `undefined`
   * when the landscape is not present in the cache at all (e.g. a build for
   * a hero product nobody has open); `null` is a real cached value -- a
   * landscape with no run row yet -- and is compared like any other status.
   */
  getCachedStatus: (landscapeId: string) => LandscapeRunStatus | null | undefined;
  /** Invalidates the `['landscapes']` prefix -- one fan-out, not a patch. */
  invalidate: () => void;
  /** Writes only `status_info` for this landscape id into the cache. */
  patchStatusInfo: (landscapeId: string, statusInfo: string) => void;
}

/**
 * The whole of what a `.landscape.status.updated` broadcast does, in one
 * place. Exactly one comparison decides a mapped, non-terminal event: if the
 * mapped status differs from what is already cached, invalidate; if only
 * `status_info` differs, patch it in and issue no refetch. A terminal (or
 * unmapped) wire status always invalidates. A landscape id absent from the
 * cache entirely is left alone -- there is nothing to patch and nothing
 * gained by invalidating a query for a landscape nothing has fetched.
 */
export function handleLandscapeBroadcast(
  event: LandscapeBroadcastPayload,
  effects: LandscapeBroadcastEffects,
): void {
  const landscapeId = String(event.landscape_id);
  const mappedStatus = mapLandscapeBroadcastStatus(event.status);

  if (mappedStatus === null) {
    effects.invalidate();
    return;
  }

  const cachedStatus = effects.getCachedStatus(landscapeId);
  if (cachedStatus === undefined) return;

  if (cachedStatus !== mappedStatus) {
    effects.invalidate();
    return;
  }

  effects.patchStatusInfo(landscapeId, event.status_info);
}

/**
 * `.landscape.products.updated`: the identity fill wrote a title/brand/image.
 * The only cache entry that can show it is the open landscape's detail. The
 * detail query keys on the route-param *string* (`useGetLandscapeDetail`), so
 * the numeric wire id is converted -- a number key would never prefix-match.
 */
export function landscapeProductsUpdatedInvalidation(event: { landscape_id: number }): {
  queryKey: readonly ['landscape-detail', string];
} {
  return { queryKey: ['landscape-detail', String(event.landscape_id)] };
}
